import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  Container,
  VStack,
  HStack,
  Box,
  Heading,
  Text,
  Button,
  Spinner,
  Alert,
  Tabs,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiPlus, FiEdit2, FiTrash2, FiRefreshCw } from 'react-icons/fi'
import { useContactLists, useDeleteContactList, useMaterializeList } from '../hooks/useContactList'
import { useSavedSearches, useDeleteSavedSearch } from '../hooks/useSavedSearch'
import { useJobPolling } from '../hooks/useJobPolling'
import { ResponsiveTable, type ResponsiveColumn, StatusChip, Card, type StatusVariant } from '../components/shared'
import { formatShortDate, formatRelativeDate } from '../lib/formatDate'
import type { ContactListSummary } from '../types/contactList'
import { canManuallyRefresh } from '../lib/listRefresh'
import type { SavedSearchSummary } from '../types/savedSearch'

const strategyChipVariant: Record<string, StatusVariant> = {
  Dynamic: 'info',
  Manual: 'neutral',
  AutoDaily: 'success',
  Locked: 'warning',
}

type DialogState = {
  type: 'deleteList' | 'refreshList' | 'deleteSearch' | null
  id: string | null
  name: string | null
}

export function ListsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const cancelRef = useRef<HTMLButtonElement>(null)

  // Contact Lists (primary)
  const { data: allLists, isLoading: isLoadingLists, error: listError } = useContactLists()
  const deleteListMutation = useDeleteContactList()
  const materializeMutation = useMaterializeList()

  // Materialize now enqueues a background job (WI-42) instead of running inline. refreshListId
  // is captured alongside the jobId because dialogState (which also carries the id) is reset
  // once the dialog closes on terminal -- the completion effect below needs its own copy to
  // invalidate the right per-list caches.
  const [refreshJobId, setRefreshJobId] = useState<string | null>(null)
  const [refreshListId, setRefreshListId] = useState<string | null>(null)
  const { data: refreshJob, isTerminal: refreshTerminal } = useJobPolling(refreshJobId)
  const isRefreshing = materializeMutation.isPending || (!!refreshJobId && !refreshTerminal)

  // Filter to standalone lists only (no campaignId = not a shadow copy)
  const standaloneLists = allLists?.filter(l => !(l as any).campaignId)

  // Saved Searches (templates)
  const { data: searches, isLoading: isLoadingSearches, error: searchError } = useSavedSearches()
  const deleteSearchMutation = useDeleteSavedSearch()

  const [dialogState, setDialogState] = useState<DialogState>({
    type: null,
    id: null,
    name: null,
  })

  // --- List handlers ---
  const handleEditList = (list: ContactListSummary) => {
    navigate(`/dashboard/lists/${list.id}/edit`)
  }

  const handleRefreshListClick = (list: ContactListSummary) => {
    // Only effectively-Manual lists support on-demand refresh; no-op otherwise.
    if (!canManuallyRefresh(list)) return
    setDialogState({ type: 'refreshList', id: list.id, name: list.label })
  }

  const handleDeleteListClick = (list: ContactListSummary) => {
    setDialogState({ type: 'deleteList', id: list.id, name: list.label })
  }

  const handleConfirmRefreshList = async () => {
    if (!dialogState.id) return
    try {
      const { jobId } = await materializeMutation.mutateAsync(dialogState.id)
      // Enqueued (202) -- not refreshed yet. Keep the dialog open (its button stays isLoading
      // via isRefreshing) through the poll; the completion effect below closes it and fires the
      // success/error toast once the job actually reaches a terminal state.
      setRefreshListId(dialogState.id)
      setRefreshJobId(jobId)
    } catch {
      // Failed to even enqueue (e.g. network/validation error on the POST itself).
      toaster.create({ title: 'Refresh failed', type: 'error', duration: 5000 })
      setDialogState({ type: null, id: null, name: null })
    }
  }

  // Drives the materialize job to completion (WI-42). Branches on `!refreshTerminal` alone --
  // NOT `!refreshTerminal || !refreshJob` -- because a 404 (job not owned / not found) reports
  // isTerminal=true with job undefined; guarding on `!refreshJob` too would swallow that case and
  // leave the dialog spinning forever. Invalidation happens here, on completion, never on the
  // enqueue mutation's onSuccess -- materialize genuinely persists (rewrites list membership,
  // stamps IsMaterialized/memberCount), so invalidating before the job finishes would just
  // refetch stale pre-job data.
  useEffect(() => {
    if (!refreshTerminal) return

    if (refreshJob?.status === 'Succeeded') {
      queryClient.invalidateQueries({ queryKey: ['lists'] })
      if (refreshListId) {
        queryClient.invalidateQueries({ queryKey: ['lists', refreshListId] })
        queryClient.invalidateQueries({ queryKey: ['lists', refreshListId, 'overview'] })
      }
      toaster.create({ title: 'List refreshed', type: 'success', duration: 4000 })
    } else {
      toaster.create({
        title: 'Refresh failed',
        description: refreshJob?.errorMessage ?? 'An error occurred.',
        type: 'error',
        duration: 5000,
      })
    }
    setRefreshJobId(null)
    setRefreshListId(null)
    setDialogState({ type: null, id: null, name: null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTerminal, refreshJob])

  const handleConfirmDeleteList = async () => {
    if (!dialogState.id) return
    try {
      await deleteListMutation.mutateAsync(dialogState.id)
      toaster.create({ title: 'List deleted', type: 'success', duration: 4000 })
    } catch {
      toaster.create({ title: 'Delete failed', type: 'error', duration: 5000 })
    } finally {
      setDialogState({ type: null, id: null, name: null })
    }
  }

  // --- Search template handlers ---
  const handleEditSearch = (search: SavedSearchSummary) => {
    navigate(`/dashboard/searches/${search.id}/edit`)
  }

  const handleDeleteSearchClick = (search: SavedSearchSummary) => {
    setDialogState({ type: 'deleteSearch', id: search.id, name: search.label })
  }

  const handleConfirmDeleteSearch = async () => {
    if (!dialogState.id) return
    try {
      await deleteSearchMutation.mutateAsync(dialogState.id)
      toaster.create({ title: 'Template deleted', type: 'success', duration: 4000 })
    } catch {
      toaster.create({ title: 'Delete failed', type: 'error', duration: 5000 })
    } finally {
      setDialogState({ type: null, id: null, name: null })
    }
  }

  const handleCloseDialog = () => {
    setDialogState({ type: null, id: null, name: null })
  }

  const listColumns: ResponsiveColumn<ContactListSummary>[] = [
    {
      header: 'Name',
      mobileRole: 'title',
      width: '40%',
      cell: (r) => (
        <>
          <Text fontWeight="medium">{r.label}</Text>
          {r.description && (
            <Text fontSize="xs" color="muted" lineClamp={1}>
              {r.description}
            </Text>
          )}
        </>
      ),
    },
    {
      header: 'Members',
      mobileRole: 'badge',
      isNumeric: true,
      cell: (r) =>
        r.memberCount != null ? (
          <StatusChip variant={r.isMaterialized ? 'success' : 'neutral'}>
            {r.memberCount.toLocaleString()}
          </StatusChip>
        ) : (
          <Text color="muted" fontSize="xs">-</Text>
        ),
    },
    {
      header: 'Strategy',
      mobileRole: 'badge',
      cell: (r) => (
        <StatusChip variant={strategyChipVariant[r.refreshStrategy] ?? 'neutral'}>
          {r.refreshStrategy}
        </StatusChip>
      ),
    },
    {
      header: 'Last Refreshed',
      mobileRole: 'meta',
      cell: (r) => (
        <Text fontSize="sm" color="muted">
          {r.lastRefreshed ? formatRelativeDate(r.lastRefreshed) : 'Never'}
        </Text>
      ),
    },
    {
      header: 'Created',
      mobileRole: 'meta',
      cell: (r) => (
        <Text fontSize="sm" color="muted">
          {formatShortDate(r.createdAt)}
        </Text>
      ),
    },
  ]

  const templateColumns: ResponsiveColumn<SavedSearchSummary>[] = [
    {
      header: 'Name',
      mobileRole: 'title',
      width: '40%',
      cell: (r) => (
        <>
          <Text fontWeight="medium">{r.label}</Text>
          {r.description && (
            <Text fontSize="xs" color="muted" lineClamp={1}>
              {r.description}
            </Text>
          )}
        </>
      ),
    },
    {
      header: 'Filters',
      mobileRole: 'meta',
      cell: (r) => (
        <Text fontSize="sm">
          {r.filterCount} condition{r.filterCount !== 1 ? 's' : ''}
        </Text>
      ),
    },
    {
      header: 'Created By',
      mobileRole: 'meta',
      cell: (r) => <Text fontSize="sm">{r.createdByName}</Text>,
    },
    {
      header: 'Created',
      mobileRole: 'meta',
      cell: (r) => (
        <Text fontSize="sm" color="muted">
          {formatShortDate(r.createdAt)}
        </Text>
      ),
    },
  ]

  return (
    <Container maxW="container.xl" py={8}>
      <VStack gap={6} align="stretch">
        <Box>
          <Heading size="lg">Lists</Heading>
          <Text color="muted" mt={1}>
            Manage contact lists and reusable search templates
          </Text>
        </Box>

        <Tabs.Root colorPalette="brand" defaultValue="lists">
          <Tabs.List>
            <Tabs.Trigger value="lists">Lists</Tabs.Trigger>
            <Tabs.Trigger value="templates">Templates</Tabs.Trigger>
          </Tabs.List>

          <Tabs.ContentGroup>
            {/* === LISTS TAB (Primary) === */}
            <Tabs.Content value="lists" px={0}>
              <HStack justify="flex-end" mb={4}>
                <Button
                  variant="outline"
                  colorPalette="brand"
                  onClick={() => navigate('/dashboard/lists/guided')}
                >
                  Build for your candidate
                </Button>
                <Button colorPalette="brand" onClick={() => navigate('/dashboard/lists/new')}><FiPlus />New List
                                  </Button>
              </HStack>

              {isLoadingLists && (
                <VStack py={12}>
                  <Spinner size="lg" color="accent" />
                  <Text color="muted">Loading contact lists...</Text>
                </VStack>
              )}

              {!!listError && (
                <Alert.Root status="error" borderRadius="md">
                  <Alert.Indicator />
                  Failed to load contact lists. Please try again.
                </Alert.Root>
              )}

              {!isLoadingLists && standaloneLists && standaloneLists.length === 0 && (
                <Card p={12} textAlign="center">
                  <Text color="muted" mb={4}>
                    No contact lists yet. Create your first list to start organizing contacts.
                  </Text>
                  <HStack justify="center">
                    <Button
                      variant="outline"
                      colorPalette="brand"
                      onClick={() => navigate('/dashboard/lists/guided')}
                    >
                      Build for your candidate
                    </Button>
                    <Button colorPalette="brand" onClick={() => navigate('/dashboard/lists/new')}><FiPlus />New List
                                          </Button>
                  </HStack>
                </Card>
              )}

              {!isLoadingLists && standaloneLists && standaloneLists.length > 0 && (
                <ResponsiveTable
                  columns={listColumns}
                  rows={standaloneLists}
                  keyFn={(r) => r.id}
                  onRowClick={(r) => navigate(`/dashboard/lists/${r.id}`)}
                  rowActions={[
                    { label: 'Edit', icon: FiEdit2, onClick: handleEditList },
                    { label: 'Refresh', icon: FiRefreshCw, onClick: handleRefreshListClick, hidden: (r) => !canManuallyRefresh(r) },
                    { label: 'Delete', icon: FiTrash2, destructive: true, onClick: handleDeleteListClick },
                  ]}
                />
              )}
            </Tabs.Content>

            {/* === TEMPLATES TAB (Secondary) === */}
            <Tabs.Content value="templates" px={0}>
              <HStack justify="flex-end" mb={4}>
                <Button colorPalette="brand" onClick={() => navigate('/dashboard/searches/new')}><FiPlus />New Template
                                  </Button>
              </HStack>

              {isLoadingSearches && (
                <VStack py={12}>
                  <Spinner size="lg" color="accent" />
                  <Text color="muted">Loading search templates...</Text>
                </VStack>
              )}

              {!!searchError && (
                <Alert.Root status="error" borderRadius="md">
                  <Alert.Indicator />
                  Failed to load search templates. Please try again.
                </Alert.Root>
              )}

              {!isLoadingSearches && searches && searches.length === 0 && (
                <Card p={12} textAlign="center">
                  <Text color="muted" mb={4}>
                    No search templates yet. Templates let you save and reuse filter configurations.
                  </Text>
                  <Button colorPalette="brand" onClick={() => navigate('/dashboard/searches/new')}><FiPlus />New Template
                                      </Button>
                </Card>
              )}

              {!isLoadingSearches && searches && searches.length > 0 && (
                <ResponsiveTable
                  columns={templateColumns}
                  rows={searches}
                  keyFn={(r) => r.id}
                  onRowClick={(r) => navigate(`/dashboard/searches/${r.id}/edit`)}
                  rowActions={[
                    { label: 'Edit', icon: FiEdit2, onClick: handleEditSearch },
                    { label: 'Delete', icon: FiTrash2, destructive: true, onClick: handleDeleteSearchClick },
                  ]}
                />
              )}
            </Tabs.Content>
          </Tabs.ContentGroup>
        </Tabs.Root>
      </VStack>

      {/* Refresh List Dialog */}
      <Dialog.Root
        open={dialogState.type === 'refreshList'}
        initialFocusEl={() => cancelRef.current}
        closeOnInteractOutside={!isRefreshing}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open)
            { if (!isRefreshing) handleCloseDialog() }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">
                  Refresh List
                </Dialog.Header>
                <Dialog.Body>
                  Are you sure you want to refresh <strong>"{dialogState.name}"</strong>? This will
                  re-evaluate the search criteria and update the member list.
                </Dialog.Body>
                <Dialog.Footer>
                  <Button ref={cancelRef} onClick={handleCloseDialog} disabled={isRefreshing}>
                    Cancel
                  </Button>
                  <Button
                    colorPalette="green"
                    onClick={handleConfirmRefreshList}
                    ml={3}
                    loading={isRefreshing}
                  >
                    Refresh
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>

      {/* Delete List Dialog */}
      <Dialog.Root
        open={dialogState.type === 'deleteList'}
        initialFocusEl={() => cancelRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            handleCloseDialog();
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">
                  Delete List
                </Dialog.Header>
                <Dialog.Body>
                  Are you sure you want to delete <strong>"{dialogState.name}"</strong>? This action
                  cannot be undone.
                </Dialog.Body>
                <Dialog.Footer>
                  <Button ref={cancelRef} onClick={handleCloseDialog}>
                    Cancel
                  </Button>
                  <Button
                    colorPalette="red"
                    onClick={handleConfirmDeleteList}
                    ml={3}
                    loading={deleteListMutation.isPending}
                  >
                    Delete
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>

      {/* Delete Template Dialog */}
      <Dialog.Root
        open={dialogState.type === 'deleteSearch'}
        initialFocusEl={() => cancelRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            handleCloseDialog();
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">
                  Delete Template
                </Dialog.Header>
                <Dialog.Body>
                  Are you sure you want to delete <strong>"{dialogState.name}"</strong>? This action
                  cannot be undone.
                </Dialog.Body>
                <Dialog.Footer>
                  <Button ref={cancelRef} onClick={handleCloseDialog}>
                    Cancel
                  </Button>
                  <Button
                    colorPalette="red"
                    onClick={handleConfirmDeleteSearch}
                    ml={3}
                    loading={deleteSearchMutation.isPending}
                  >
                    Delete
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>
    </Container>
  );
}
